import { useDrop } from 'react-dnd';
import { NativeTypes } from 'react-dnd-html5-backend';
import { Trans } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

import { getDataSize, getTestProps } from '../../../lib/helpers';

import FileButton from '../../../components/FileButton/FileButton';
import HelpErrorTextsTemplate from '../../../components/HelpErrorTextsTemplate/HelpErrorTextsTemplate';

import { ImportIconCircle } from '../../../images/shapes';

import { useFieldContext } from '../../form';
import { useFieldErrors } from '../../useFieldErrors';
import FileSelectorItem from './FileSelectorItem/FileSelectorItem';

interface FilesSelectorFieldProps {
  /**
   * Accepted file types
   */
  accept: string;
  /**
   * DnD file handler
   */
  dropFileHandler: (files: File[]) => void;
  /**
   * DnD label
   */
  dndLabel: React.ReactNode;
  /**
   * Is file input multiple
   */
  multiple?: boolean;
  /**
   * File selector test id
   */
  testId?: string;
}

const FilesSelectorField = ({
  accept,
  dropFileHandler,
  dndLabel,
  multiple = false,
  testId = '',
}: FilesSelectorFieldProps) => {
  const field = useFieldContext<File[]>();
  const errors = useFieldErrors(field.name);

  const value = field.state.value || [];

  const [{ canDrop, isOver }, drop] = useDrop(
    () => ({
      accept: [NativeTypes.FILE],
      drop(item: { files: File[] }) {
        dropFileHandler(item.files);
      },
      canDrop: () => true,
      collect: (monitor) => ({
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop(),
      }),
    }),
    [],
  );

  const removeFile = (idx: number) => {
    field.removeValue(idx);
    field.handleBlur();
  };

  const onAddFile = async (payload: null | FileList | File[]) => {
    Array.from(payload || []).forEach((file) => {
      if (multiple) {
        field.pushValue(file);
      } else {
        field.setValue([file]);
      }
    });

    field.handleBlur();
  };

  return (
    <div
      ref={() => {
        drop(document.body);
      }}
      {...getTestProps(testId, 'file-selector-container')}
    >
      <div
        className={twMerge(
          'grid grid-cols-1 gap-3 my-2 transition-all',
          value.length > 1 && 'md:grid-cols-2',
          value.length && 'my-4',
        )}
      >
        {value?.map((item, index) => (
          <FileSelectorItem
            idx={index}
            fileName={item.name}
            onRemove={removeFile}
            fileSize={getDataSize(item.size)}
            testId={testId}
            key={`file-selector-container-${item.name}-${item.lastModified}-${index}`}
          />
        ))}
      </div>

      <div
        className={twMerge(
          'flex flex-col rounded-md items-center justify-center px-2 ' +
            'py-4 md:px-6 md:py-6 border border-dashed border-slate-400',
          canDrop && isOver && 'border-blue bg-slate-50 dark:bg-slate-800 ',
          !multiple && value.length >= 1 && 'hidden',
        )}
      >
        <ImportIconCircle className="mb-3 h-8 w-8" />
        <div className="inline-flex text-sm md:text-base">
          <Trans
            i18nKey="SpaceSynchronization.Import.DnD.Paragraph"
            components={{
              1: (
                <FileButton
                  onUpload={onAddFile}
                  testId={testId}
                  buttonColor="borderless"
                  additionalClasses="mx-1 text-blue hover:text-blue dark:text-blue text-sm  md:text-base"
                  accept={accept}
                  multiple={multiple}
                />
              ),
            }}
          >
            <FileButton
              onUpload={onAddFile}
              testId={testId}
              buttonColor="borderless"
              additionalClasses="mx-1 text-blue hover:text-blue dark:text-blue"
              accept={accept}
              multiple={multiple}
            />
          </Trans>
        </div>
        <span className="text-slate-400 text-sm  md:text-base">{dndLabel}</span>
      </div>
      <HelpErrorTextsTemplate error={errors} />
    </div>
  );
};

export default FilesSelectorField;
